import React from 'react';
import merge from 'lodash.merge';
import i18n from 'i18next';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import Lens from '@material-ui/icons/Lens';

import { formatNumber } from 'helpers/helpers';
import withLoading from 'components/Loading/withLoading';
import { CalculationMode } from 'common/interfaces';

import { rentGraphColors, RentData } from 'stores/workflowStores/finances/rent/model';
import { borderRadiusBox } from 'components/AppTheme';
import { Currency } from 'enums';

type StackingOptions = undefined | 'normal' | 'percent';

const styles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      fontSize: '9pt',
      fontWeight: 'bold',
      textAlign: 'left',
      color: theme.palette.common.black,
      display: 'flex',
      justifyContent: 'space-evenly',
      '& > span': {
        display: 'inline-flex',
        alignItems: 'center',
        '& span': {
          fontSize: '11pt',
          marginRight: theme.spacing(0.5),
          '& svg': {
            position: 'relative',
            top: '2px',
          },
        },
      },
    },
    graph: {
        '& g._tooltip': {
          display: 'none'
        },
        '& ._tooltip > span ': {
          background: '#f8f8f8',
          boxShadow: '0 0 10px 1px rgba(0,0,0,0.13)',
          borderRadius: borderRadiusBox,
          padding: theme.spacing(1),
          fontFamily: 'Roboto, sans-serif',
          display: 'inline-flex',
          alignItems: 'center',
          '& b': {
            marginLeft: '2px'
          }
        },
        '& ._tooltip .dot': {
          display: 'inline-block',
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          marginRight: '8px'
        }

    }
  }),
);

interface Props {
  data?: RentData;
  xCategories?: string[];
  translatedCurrency: string;
  currency?: Currency;
  plan?: boolean;
  original?: boolean;
  settings?: Highcharts.Options;
}

export interface BarData {
  name: string;
  data: number[];
  stacking?: StackingOptions;
  color?: string;
}

interface GraphData {
  allowance?: number;
  portfolioRent?: number;
  retirementIncome?: number;
  requiredRent?: number;
}

const BarGraph: React.FC<Props> = ({ data, xCategories, currency, plan, original, settings }) => {
  const classes = styles();

  const formatData = (): BarData[] => {
    if (!data) return [];
    const notStacked = ['requiredRent'];
    const graphData: GraphData = {};

    switch (data.portfolioRentCalculationMode) {
      case CalculationMode.CALCULATED:
        graphData.allowance = data.calculatedPortfolioRent.value;
        break;
      case CalculationMode.MANUAL:
        graphData.allowance = data.plannedPortfolioRent.value;
        break;
      case CalculationMode.REQUIRED:
        if (data.requiredPortfolioRent) {
          graphData.allowance = data.requiredPortfolioRent.value;
        }
        break;
    }

    graphData.retirementIncome = data.retirementIncome.value;
    graphData.requiredRent =
      data.requiredRentCalculationMode === CalculationMode.CALCULATED
        ? data.calculatedRequiredRent.value
        : data.plannedRequiredRent.value;

    return Object.keys(graphData).map((key) => ({
      name: i18n.t(`finQuestionnary_rent_graph_${key}`),
      stacking: notStacked.includes(key) ? undefined : 'normal',
      color: original && rentGraphColors[key].original ? rentGraphColors[key].original : rentGraphColors[key].default,
      data: [(graphData as any)[key]],
    }));
  };

  const formattedData = formatData();

  const renderLegend = (data: BarData[]) => {
    return (
      <div className={classes.root}>
        {data.map((dp) => (
          <span key={dp.name} style={{ fontSize: '14px', fontWeight: 800, fontFamily: 'Roboto, sans-serif' }}>
            <span style={{ color: dp.color }}>
              <Lens color="inherit" fontSize="inherit" />
            </span>
            {dp.name}: {formatNumber(dp.data[0])} {currency}
          </span>
        ))}
      </div>
    );
  };

  const defaultSettings: Highcharts.Options = {
    title: {
      text: '',
    },
    credits: {
      enabled: false,
    },
    chart: {
      type: 'bar',
      spacingLeft: 0,
      spacingTop: 0,
      spacingBottom: 0,
      spacingRight: 0,
      height: '62',
      marginTop: 0,
      marginRight: 0
    },
    tooltip: {
      useHTML: true,
      className: '_tooltip',
      headerFormat:
        typeof xCategories === 'undefined'
          ? ''
          : '<span style="font-size: 10px">{point.key}</span><br/>',
      pointFormat: `<span style="background-color:{point.color}" class="dot">&nbsp;</span> {series.name}: <b>{point.y} ${currency}</b><br/>`,
    },
    plotOptions: {
      series: {
        stacking: undefined,
      },
    },
    legend: {
      enabled: false,
    },
    xAxis: {
      lineWidth: 0,
      gridLineWidth: 0,
      minorGridLineWidth: 0,
      lineColor: 'transparent',
      minorTickLength: 0,
      tickLength: 0,
      categories: xCategories,
      labels: {
        enabled: false,
      },
      minPadding: 0,
      maxPadding: 0,
    },
    yAxis: {
      lineWidth: 0,
      gridLineWidth: 0,
      minorGridLineWidth: 0,
      lineColor: 'transparent',
      minorTickLength: 0,
      tickLength: 0,
      min: 0,
      title: {
        text: '',
      },
      labels: {
        enabled: false,
      },
      minPadding: 0,
      maxPadding: 0,
    },
    series: formattedData as any,
  };

  const graphSettings = settings ? merge({}, defaultSettings, settings) : defaultSettings;

  return (
    <>
      <div className={classes.graph}><HighchartsReact highcharts={Highcharts} options={graphSettings as Highcharts.Options} /></div>
      {renderLegend(formattedData)}
    </>
  );
};

export default React.memo(withLoading(BarGraph));
